class LordAnnouncementMarquee extends HTMLElement {
  #resizeObserver;
  #mutationObserver;
  #reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
  #scheduled = false;

  connectedCallback() {
    this.#resizeObserver = new ResizeObserver(() => this.#schedule());
    this.#mutationObserver = new MutationObserver(() => this.#schedule());
    const viewport = this.querySelector('.lord-announcement-marquee__viewport');
    if (viewport) this.#resizeObserver.observe(viewport);
    this.#observeContent();
    this.#reducedMotion.addEventListener('change', this.#schedule);
    this.#schedule();
    document.fonts?.ready.then(() => this.#schedule());
  }

  disconnectedCallback() {
    this.#resizeObserver?.disconnect();
    this.#mutationObserver?.disconnect();
    this.#reducedMotion.removeEventListener('change', this.#schedule);
  }

  #observeContent() {
    this.#mutationObserver?.observe(this, { childList: true, subtree: true, characterData: true });
  }

  #schedule = () => {
    if (this.#scheduled || !this.isConnected) return;
    this.#scheduled = true;
    requestAnimationFrame(() => {
      this.#scheduled = false;
      this.#build();
    });
  };

  #accessibleClone(node) {
    const copy = node.cloneNode(true);
    copy.setAttribute('aria-hidden', 'true');
    copy.setAttribute('data-lord-marquee-copy', '');
    for (const element of [copy, ...copy.querySelectorAll('*')]) {
      element.removeAttribute('id');
      for (const attribute of [...element.attributes]) {
        if (attribute.name.startsWith('data-shopify-')) element.removeAttribute(attribute.name);
      }
      if (element.matches('a, button, input, select, textarea, [tabindex]')) {
        element.setAttribute('tabindex', '-1');
      }
    }
    return copy;
  }

  #build() {
    const viewport = this.querySelector('.lord-announcement-marquee__viewport');
    const track = this.querySelector('.lord-announcement-marquee__track');
    const group = this.querySelector('.lord-announcement-marquee__group');
    if (!viewport || !track || !group) return;

    this.#mutationObserver?.disconnect();
    this.removeAttribute('data-ready');
    group.querySelectorAll('[data-lord-marquee-copy]').forEach((node) => node.remove());
    track.querySelectorAll('.lord-announcement-marquee__group[data-lord-marquee-copy]').forEach((node) => node.remove());
    const originals = [...group.querySelectorAll(':scope > .announcement-bar__slide')];
    if (!originals.length || this.#reducedMotion.matches) {
      this.#observeContent();
      return;
    }

    // Fill at least one viewport so the loop never shows a blank stretch.
    for (let repeat = 0; group.scrollWidth < viewport.clientWidth * 1.2 && repeat < 16; repeat++) {
      group.append(...originals.map((slide) => this.#accessibleClone(slide)));
    }

    const twin = this.#accessibleClone(group);
    track.append(twin);
    const distance = group.getBoundingClientRect().width;
    const speed = Math.min(10, Math.max(2, Number(this.dataset.speed) || 5));
    if (distance > 0) {
      this.style.setProperty('--lord-marquee-distance', `${distance}px`);
      this.style.setProperty('--lord-marquee-duration', `${Math.max(12, distance / (speed * 11))}s`);
      this.setAttribute('data-ready', '');
    }
    this.#observeContent();
  }
}

if (!customElements.get('lord-announcement-marquee')) {
  customElements.define('lord-announcement-marquee', LordAnnouncementMarquee);
}
